import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import DirectionsBusFilledOutlinedIcon from "@mui/icons-material/DirectionsBusFilledOutlined";
import DirectionsSubwayFilledOutlinedIcon from "@mui/icons-material/DirectionsSubwayFilledOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import DirectionsBoatFilledOutlinedIcon from "@mui/icons-material/DirectionsBoatFilledOutlined";
import AirplanemodeActiveOutlinedIcon from "@mui/icons-material/AirplanemodeActiveOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SouthEastOutlinedIcon from "@mui/icons-material/SouthEastOutlined";
import NorthEastOutlinedIcon from "@mui/icons-material/NorthEastOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import WestOutlinedIcon from "@mui/icons-material/WestOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ViewDayOutlinedIcon from "@mui/icons-material/ViewDayOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import TourOutlinedIcon from "@mui/icons-material/TourOutlined";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ModeOfTravelOutlinedIcon from "@mui/icons-material/ModeOfTravelOutlined";
import {
  ActivityEventObj,
  BusEventObj,
  CarRentalEventObj,
  CityGuideEventObj,
  CruiseEventObj,
  DiningEventObj,
  FlightEventObj,
  InfoEventObj,
  LodgingEventObj,
  OtherTransportsEventObj,
  RailEventObj,
} from "./interfaces";
import { nanoid } from "nanoid";
import { getLocalTimezone } from "../utils";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";

// event types
// value only matters
export const EVENT_TYPES = {
  activity: "activity",
  dining: "dining",
  lodging: "lodging",
  flight: "flight",
  cruise: "cruise",
  rail: "rail",
  bus: "bus",
  carRental: "carRental",
  otherTransports: "otherTransports",
  info: "info",
  cityGuide: "cityGuide",
} as const;

export const EVENT_SUB_TYPES = {
  checkIn: "checkIn",
  checkOut: "checkOut",
  departure: "departure",
  arrival: "arrival",
  pickUp: "pickUp",
  dropOff: "dropOff",
} as const;

// modal types
export const MODAL_TYPES = {
  [EVENT_TYPES.activity]: EVENT_TYPES.activity,
  [EVENT_TYPES.dining]: EVENT_TYPES.dining,
  [EVENT_TYPES.lodging]: EVENT_TYPES.lodging,
  [EVENT_TYPES.flight]: EVENT_TYPES.flight,
  [EVENT_TYPES.cruise]: EVENT_TYPES.cruise,
  [EVENT_TYPES.rail]: EVENT_TYPES.rail,
  [EVENT_TYPES.bus]: EVENT_TYPES.bus,
  [EVENT_TYPES.carRental]: EVENT_TYPES.carRental,
  [EVENT_TYPES.otherTransports]: EVENT_TYPES.otherTransports,
  [EVENT_TYPES.info]: EVENT_TYPES.info,
  [EVENT_TYPES.cityGuide]: EVENT_TYPES.cityGuide,
  tripInfo: "tripInfo",
  autoFlight: "autoFlight",
  libraryView: "libraryView",
} as const;

// modal kinds
export const MODAL_KINDS = {
  add: "add",
  edit: "edit",
  libraryEdit: "libraryEdit",
} as const;

// section types
export const SECTION_TYPES = {
  infoSection: "infoSection",
  daySection: "daySection",
} as const;

// idea type
export const IDEA_TYPE = "idea" as const;

// tour type
export const TOUR_TYPE = "tour" as const;

// cruise library type
export const CRUISE_LIBRARY_TYPE = "cruiseLibrary" as const;

// city guide group
export const CITY_GUIDE_GROUP = "cityGuideGroup" as const;

export const IDEA_TYPE_DETAILS = {
  Icon: TipsAndUpdatesOutlinedIcon,
  bgColor: "#fef3c7",
  color: "#f59e0b",
};

export const INITIAL_INFO_SECTIONS_POSITIONS_INDEX = 0;
export const DAY_SECTIONS_POSITIONS_INDEX = 1;
export const END_INFO_SECTIONS_POSITIONS_INDEX = 2;

export const UNTITLED_SECTION = "Untitled Section";

// all sections
/** mapping between Icon */
export const SECTION_TYPE_DETAILS = {
  [SECTION_TYPES.daySection]: {
    name: "Day",
    Icon: CalendarMonthOutlinedIcon,
    bgColor: "#fee2e2",
    color: "#ef4444",
  },
  [SECTION_TYPES.infoSection]: {
    name: "Info",
    Icon: ViewDayOutlinedIcon,
    bgColor: "#e0e7ff",
    color: "#6366f1",
  },
};

export const TOUR_TYPE_DETAILS = {
  name: "Tour",
  Icon: TourOutlinedIcon,
  bgColor: "#dbeafe", // blue 100
  color: "#3b82f6", // blue 500
};

export const CITY_GUIDE_GROUP_DETAILS = {
  name: "City Guide Group",
  Icon: LibraryBooksOutlinedIcon,
  bgColor: "#fae8ff", // fuschia 100
  color: "#d946ef", // fuschia 500
};

// all events
/** mapping between Icon, name, modalType */
export const EVENT_TYPES_DETAILS = {
  [EVENT_TYPES.activity]: {
    name: "Activity",
    Icon: EventNoteOutlinedIcon,
    bgColor: "#e0e7ff", // indigo 100
    color: "#6366f1", // indigo 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.activity,
        bookedThrough: "",
        confirmation: "",
        provider: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as ActivityEventObj;
    },
  },
  [EVENT_TYPES.dining]: {
    name: "Dining",
    Icon: LocalDiningOutlinedIcon,
    bgColor: "#fce7f3", // pink 100
    color: "#ec4899", // pink 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.dining,
        bookedThrough: "",
        confirmation: "",
        provider: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as DiningEventObj;
    },
  },
  [EVENT_TYPES.lodging]: {
    name: "Lodging",
    Icon: HotelOutlinedIcon,
    bgColor: "#e0f2fe", // sky 100
    color: "#0ea5e9", // sky 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.lodging,
        subType: EVENT_SUB_TYPES.checkIn,
        bookedThrough: "",
        confirmation: "",
        roomOrBedType: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as LodgingEventObj;
    },
  },
  [EVENT_TYPES.flight]: {
    name: "Flight",
    Icon: AirplanemodeActiveOutlinedIcon,
    bgColor: "#dcfce7", // green 100
    color: "#22c55e", // green 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        departureDate: null,
        departureTime: "",
        arrivalDate: null,
        arrivalTime: "",
        arrivalGate: "",
        arrivalTerminal: "",
        departureGate: "",
        departureTerminal: "",
        arrivalTimezone: getLocalTimezone(),
        departureTimezone: getLocalTimezone(),
        ciriumId: "",
        notes: "",
        type: EVENT_TYPES.flight,
        bookedThrough: "",
        confirmation: "",
        airline: "",
        flightNumber: "",
        seatOrTicketDetails: "",
        duration: "",
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
        arrivalCity: "",
        departureCity: "",
      } as FlightEventObj;
    },
  },
  [EVENT_TYPES.bus]: {
    Icon: DirectionsBusFilledOutlinedIcon,
    name: "Bus",
    bgColor: "#fef3c7", // amber 100
    color: "#f59e0b", // amber 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.bus,
        subType: EVENT_SUB_TYPES.departure,
        bookedThrough: "",
        confirmation: "",
        carrier: "",
        busNumber: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as BusEventObj;
    },
  },
  [EVENT_TYPES.rail]: {
    Icon: DirectionsSubwayFilledOutlinedIcon,
    name: "Rail",
    bgColor: "#ffedd5", // rail 100
    color: "#f97316", // rail 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.rail,
        subType: EVENT_SUB_TYPES.departure,
        bookedThrough: "",
        confirmation: "",
        carrier: "",
        railNumber: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as RailEventObj;
    },
  },
  [EVENT_TYPES.carRental]: {
    Icon: DirectionsCarFilledOutlinedIcon,
    name: "Car Rental",
    bgColor: "#fef9c3", // yellow 100
    color: "#eab308", // yellow 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.carRental,
        subType: EVENT_SUB_TYPES.pickUp,
        bookedThrough: "",
        confirmation: "",
        carrier: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as CarRentalEventObj;
    },
  },
  [EVENT_TYPES.otherTransports]: {
    Icon: ModeOfTravelOutlinedIcon,
    name: "Other",
    bgColor: "#ecfccb", // lime 100
    color: "#84cc16", // lime 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.otherTransports,
        subType: EVENT_SUB_TYPES.departure,
        bookedThrough: "",
        confirmation: "",
        carrier: "",
        number: "",
        time: "",
        duration: "",
        timezone: getLocalTimezone(),
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as OtherTransportsEventObj;
    },
  },
  [EVENT_TYPES.cruise]: {
    name: "Cruise",
    Icon: DirectionsBoatFilledOutlinedIcon,
    bgColor: "#cffafe", // cyan 100
    color: "#06b6d4", // cyan 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.cruise,
        bookedThrough: "",
        confirmation: "",
        carrier: "",
        cabinType: "",
        cabinNumber: "",
        departureTime: "",
        arrivalTime: "",
        departureDate: null,
        arrivalDate: null,
        departureTimezone: getLocalTimezone(),
        arrivalTimezone: getLocalTimezone(),
        duration: "",
        amount: "",
        currency: "",
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as CruiseEventObj;
    },
  },
  [EVENT_TYPES.info]: {
    name: "Info",
    Icon: ArticleOutlinedIcon,
    bgColor: "#dbeafe", // blue 100
    color: "#3b82f6", // blue 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.info,
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as InfoEventObj;
    },
  },
  [EVENT_TYPES.cityGuide]: {
    name: "City Guide",
    Icon: BookOutlinedIcon,
    bgColor: "#fae8ff", // fuschia 100
    color: "#d946ef", // fuschia 500
    getDefaultValue: () => {
      return {
        id: nanoid(),
        title: "",
        notes: "",
        type: EVENT_TYPES.cityGuide,
        allPassengers: PASSENGER_INPUTS.all,
        passengers: [],
        gallery: [],
        documents: [],
        ideasPositions: [],
      } as CityGuideEventObj;
    },
  },
};

export const EVENT_SUB_TYPES_DETAILS = {
  [EVENT_SUB_TYPES.departure]: {
    name: "Departure",
    Icon: NorthEastOutlinedIcon,
  },
  [EVENT_SUB_TYPES.arrival]: {
    name: "Arrival",
    Icon: SouthEastOutlinedIcon,
  },
  [EVENT_SUB_TYPES.checkIn]: {
    name: "Check In",
    Icon: LoginOutlinedIcon,
  },
  [EVENT_SUB_TYPES.checkOut]: {
    name: "Check Out",
    Icon: LogoutOutlinedIcon,
  },
  [EVENT_SUB_TYPES.pickUp]: {
    name: "Pick Up",
    Icon: EastOutlinedIcon,
  },
  [EVENT_SUB_TYPES.dropOff]: {
    name: "Drop Off",
    Icon: WestOutlinedIcon,
  },
};

export const EVENT_GROUP_DETAILS = {
  transportation: {
    name: "Transportation",
  },
};

// event durations
export const EVENT_DURATIONS = [
  { label: "15 mins", value: "0.25" },
  { label: "30 mins", value: "0.5" },
  { label: "45 mins", value: "0.75" },
  { label: "1 hour", value: "1" },
  { label: "2 hours", value: "2" },
  { label: "3 hours", value: "3" },
  { label: "4 hours", value: "4" },
  { label: "5 hours", value: "5" },
  { label: "6 hours", value: "6" },
  { label: "7 hours", value: "7" },
  { label: "8 hours", value: "8" },
  { label: "9 hours", value: "9" },
  { label: "10 hours", value: "10" },
  { label: "11 hours", value: "11" },
  { label: "12 hours", value: "12" },
  { label: "1 day", value: "24" },
  { label: "2 days", value: "48" },
  { label: "3 days", value: "72" },
  { label: "1 week", value: "168" },
];

// passengers input values
export const PASSENGER_INPUTS = {
  all: "all",
  limited: "limited",
} as const;

// event view types
export const EVENT_VIEW_TYPES = {
  picture: "picture",
  compact: "compact",
} as const;

export const LOCAL_TIMEZONE = {
  label: "Local Time Zone",
  value: "Local Time Zone",
  altName: "Local Time Zone",
};

export const EVENT_CARD_DROP_POSITIONS = {
  top: "top",
  bottom: "bottom",
} as const;

export const SECTION_CARD_DROP_POSITIONS = {
  top: "top",
  bottom: "bottom",
} as const;

export const DROPPABLES_IDS = {
  emptyInitialInfoSection: "emptyInitialInfoSection",
  emptyEndInfoSection: "emptyEndInfoSection",
  emptyItineraryBuilder: "emptyItineraryBuilder",
  eventCardDroppable: "eventCardDroppable",
  eventDroppable: "eventDroppable",
  sectionDroppable: "sectionDroppable",
};
